import React, { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  Edit2,
  UserCheck,
  Circle,
  Eye,
  EyeOff
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getAttendants, createAttendant, updateAttendant, deleteAttendant, getInstances } from '../lib/api';
import { getInitials, cn } from '../lib/utils';
import { toast } from 'sonner';

export const Attendants = () => {
  const [attendants, setAttendants] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAttendant, setEditingAttendant] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'attendant',
    instance_id: '',
  });

  const fetchAttendants = async () => {
    try {
      const response = await getAttendants();
      setAttendants(response.data);
    } catch (error) {
      console.error('Error fetching attendants:', error);
      toast.error('Erro ao carregar atendentes');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstances = async () => {
    try {
      const res = await getInstances();
      setInstances(res.data || []);
    } catch (error) {
      console.error('Error fetching instances:', error);
      // don't block attendants page if instances fail
    }
  };

  useEffect(() => {
    fetchAttendants();
    fetchInstances();
  }, []);

  const handleOpenDialog = (attendant = null) => {
    if (attendant) {
      setEditingAttendant(attendant);
      setFormData({
        name: attendant.name,
        email: attendant.email,
        password: '',
        role: attendant.role,
        instance_id: attendant.instance_id || '',
      });
    } else {
      setEditingAttendant(null);
      // Default to first instance if available
      const firstInstanceId = instances?.[0]?.id || '';
      setFormData({ name: '', email: '', password: '', role: 'attendant', instance_id: firstInstanceId });
    }
    setShowPassword(false);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingAttendant) {
        // Only send password if it was changed
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          instance_id: formData.instance_id || null,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await updateAttendant(editingAttendant.id, updateData);
        toast.success('Atendente atualizado');
      } else {
        if (!formData.password) {
          toast.error('Senha é obrigatória para novos atendentes');
          return;
        }
        if (!formData.instance_id) {
          toast.error('Selecione uma instância para o atendente');
          return;
        }
        await createAttendant(formData);
        toast.success('Atendente criado');
      }
      setDialogOpen(false);
      fetchAttendants();
    } catch (error) {
      console.error('Error saving attendant:', error);
      const message = error.response?.data?.detail || 'Erro ao salvar atendente';
      toast.error(message);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este atendente?')) return;

    try {
      await deleteAttendant(id);
      toast.success('Atendente excluído');
      fetchAttendants();
    } catch (error) {
      console.error('Error deleting attendant:', error);
      toast.error('Erro ao excluir atendente');
    }
  };

  const handleStatusChange = async (attendant, status) => {
    try {
      await updateAttendant(attendant.id, { status });
      fetchAttendants();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrador',
      supervisor: 'Supervisor',
      attendant: 'Atendente',
    };
    return labels[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-500/20 text-red-400 border-red-500/30',
      supervisor: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      attendant: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
    };
    return colors[role] || '';
  };

  const getStatusColor = (status) => {
    const colors = {
      online: 'text-green-500',
      offline: 'text-zinc-500',
      busy: 'text-yellow-500',
    };
    return colors[status] || 'text-zinc-500';
  };

  return (
    <div className="flex-1 overflow-auto p-6" data-testid="attendants-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-mono">Atendentes</h1>
            <p className="text-muted-foreground mt-1">
              Gerencie a equipe de atendimento
            </p>
          </div>
          <Button onClick={() => handleOpenDialog()} data-testid="add-attendant-btn">
            <Plus size={16} className="mr-2" />
            Novo Atendente
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {attendants.map((attendant) => (
            <Card key={attendant.id} className="bg-card border-border" data-testid={`attendant-${attendant.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Avatar className="w-12 h-12 bg-secondary">
                    <AvatarFallback className="bg-secondary text-muted-foreground">
                      {getInitials(attendant.name)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium truncate">{attendant.name}</h3>
                      <Circle
                        size={8}
                        className={cn('fill-current', getStatusColor(attendant.status))}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{attendant.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className={cn('text-xs', getRoleBadgeColor(attendant.role))}>
                        {getRoleLabel(attendant.role)}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <Select
                    value={attendant.status}
                    onValueChange={(value) => handleStatusChange(attendant, value)}
                  >
                    <SelectTrigger className="w-32 h-8 text-xs bg-secondary border-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="offline">Offline</SelectItem>
                      <SelectItem value="busy">Ocupado</SelectItem>
                    </SelectContent>
                  </Select>

                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleOpenDialog(attendant)}
                      data-testid={`edit-attendant-${attendant.id}`}
                    >
                      <Edit2 size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(attendant.id)}
                      data-testid={`delete-attendant-${attendant.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {attendants.length === 0 && !loading && (
          <div className="text-center py-12 text-muted-foreground">
            <UserCheck className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>Nenhum atendente cadastrado</p>
            <p className="text-sm mt-1">Clique em "Novo Atendente" para começar</p>
          </div>
        )}

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-mono">
                {editingAttendant ? 'Editar Atendente' : 'Novo Atendente'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Nome</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome completo"
                  className="bg-secondary border-0"
                  data-testid="attendant-name-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@exemplo.com"
                  className="bg-secondary border-0"
                  data-testid="attendant-email-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Senha {editingAttendant && <span className="text-muted-foreground">(deixe vazio para manter)</span>}
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingAttendant ? '••••••••' : 'Senha de acesso'}
                    className="bg-secondary border-0 pr-10"
                    data-testid="attendant-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Função</label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger className="bg-secondary border-0" data-testid="attendant-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="attendant">Atendente</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Apenas administradores têm acesso a todas as funcionalidades
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Instância padrão</label>
                <Select
                  value={formData.instance_id || ''}
                  onValueChange={(value) => setFormData({ ...formData, instance_id: value })}
                >
                  <SelectTrigger className="bg-secondary border-0" data-testid="attendant-instance-select">
                    <SelectValue placeholder={instances.length ? "Selecione" : "Sem instâncias"} />
                  </SelectTrigger>
                  <SelectContent>
                    {instances.map((inst) => (
                      <SelectItem key={inst.id} value={inst.id}>
                        {inst.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Usada para enviar mensagens quando a conversa não tiver instância definida.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} data-testid="save-attendant-btn">
                {editingAttendant ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Attendants;
